import { computed } from 'vue';
import type {
  PartsCreateData,
  PartsFromContoursData,
} from '@cutr/constants/part';
import {
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/vue-query';
import {
  createParts,
  createPartsFromContours,
  getParts,
  putDoNotProduce,
  putPartData,
  putPartDisplayName,
  type PartDoNotProduceData,
  type PartDisplayNameData,
  type PartUpdateData,
  type ResponseData,
} from '@/api/parts';
import { partKeys, partOperationKeys, positionKeys } from './keys';
import { useInvalidateScoped } from './shared/useInvalidateScoped';
import { useScopedQuery } from './shared/useScopedQuery';
import type { MaybeId } from './shared/types';

export function partsListOptions(orderVariantId: string) {
  return queryOptions({
    queryKey: partKeys.list(orderVariantId),
    queryFn: ({ signal }) => getParts(orderVariantId, signal),
    // The server replaces the whole graph on every write, so a deep compare of
    // thousands can be a perf hit.
    structuralSharing: false,
  });
}

export function usePartsQuery(orderVariantId: MaybeId) {
  // Thousands of nodes. `shallow` keeps them out of a deep `reactive`.
  const query = useScopedQuery(orderVariantId, partsListOptions, {
    shallow: true,
  });

  return {
    nodes: computed(() => query.data.value?.nodes ?? []),
    materialGroups: computed(() => query.data.value?.materialGroups ?? []),
    latestUpdatedAt: computed(() => query.data.value?.latestUpdatedAt ?? null),
    query,
  };
}

/**
 * The four writers all return a fresh read of the *whole* variant, so the
 * response replaces the cache outright.
 */
async function applyPartsResponse(
  queryClient: QueryClient,
  orderVariantId: string,
  response: ResponseData
) {
  // A refetch that started before the write commits would otherwise land on top
  // of this response. `latestUpdatedAt` cannot catch it: a resolving query
  // writes the cache directly, not through the updater below.
  await queryClient.cancelQueries({
    queryKey: partKeys.list(orderVariantId),
  });

  queryClient.setQueryData<ResponseData>(
    partKeys.list(orderVariantId),
    (current) =>
      current && current.latestUpdatedAt > response.latestUpdatedAt
        ? current
        : response
  );
}

/**
 * For the writers elsewhere that the parts query cannot own. The caller names
 * the variant.
 */
export function useInvalidateParts() {
  return useInvalidateScoped(partKeys.all);
}

/** The variant a write belongs to — every mutation derives its key from it. */
export type PartsScope = { orderVariantId: string };

export type MaterialsVariables = PartsScope & {
  data: Omit<PartUpdateData, 'properties'>;
};
export type PropertiesVariables = PartsScope & {
  data: Required<Pick<PartUpdateData, 'partIds' | 'properties'>>;
};
export type DisplayNameVariables = PartsScope & { data: PartDisplayNameData };
export type CreateVariables = PartsScope & {
  data: PartsCreateData;
};
export type CreateFromContoursVariables = PartsScope & {
  data: PartsFromContoursData;
};
export type DoNotProduceVariables = PartsScope & {
  data: PartDoNotProduceData;
};

export function usePartsMutations() {
  const queryClient = useQueryClient();

  const tokenFor = (orderVariantId: string) =>
    queryClient.getQueryData<ResponseData>(partKeys.list(orderVariantId))
      ?.latestUpdatedAt ?? null;

  async function onWritten(orderVariantId: string, response: ResponseData) {
    await applyPartsResponse(queryClient, orderVariantId, response);
    // Assigning a material changes the per-position aggregate. It cannot drop the
    // summary's `staleTime: 0` on its own — hardware is not a query yet (CTR-15601).
    return queryClient.invalidateQueries({
      queryKey: positionKeys.summary(orderVariantId),
    });
  }

  const materials = useMutation({
    mutationFn: ({ orderVariantId, data }: MaterialsVariables) =>
      putPartData(
        orderVariantId,
        { latestUpdatedAt: tokenFor(orderVariantId) },
        data
      ),
    onSuccess: (response, { orderVariantId }) =>
      onWritten(orderVariantId, response),
  });

  const properties = useMutation({
    mutationFn: ({ orderVariantId, data }: PropertiesVariables) =>
      putPartData(
        orderVariantId,
        { latestUpdatedAt: tokenFor(orderVariantId) },
        data
      ),
    onSuccess: (response, { orderVariantId }) =>
      onWritten(orderVariantId, response),
  });

  const displayName = useMutation({
    mutationFn: ({ orderVariantId, data }: DisplayNameVariables) =>
      putPartDisplayName(
        orderVariantId,
        { latestUpdatedAt: tokenFor(orderVariantId) },
        data
      ),
    onSuccess: (response, { orderVariantId }) =>
      onWritten(orderVariantId, response),
  });

  const doNotProduce = useMutation({
    mutationFn: ({ orderVariantId, data }: DoNotProduceVariables) =>
      putDoNotProduce(
        orderVariantId,
        { latestUpdatedAt: tokenFor(orderVariantId) },
        data
      ),
    onSuccess: (response, { orderVariantId }) =>
      onWritten(orderVariantId, response),
  });

  // Neither creator answers with the variant payload, so there is nothing to
  // write back; they invalidate and let the observer refetch.
  const create = useMutation({
    mutationFn: ({ orderVariantId, data }: CreateVariables) =>
      createParts(orderVariantId, data),
    onSuccess: (_response, { orderVariantId }) =>
      queryClient.invalidateQueries({
        queryKey: partKeys.all(orderVariantId),
      }),
  });

  const createFromContours = useMutation({
    mutationFn: ({ orderVariantId, data }: CreateFromContoursVariables) =>
      createPartsFromContours(orderVariantId, data),
    onSuccess: (_response, { orderVariantId }) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: partKeys.all(orderVariantId),
        }),
        queryClient.invalidateQueries({
          queryKey: partOperationKeys.list(orderVariantId),
        }),
      ]),
  });

  return {
    createParts: create.mutateAsync,
    createPartsFromContours: createFromContours.mutateAsync,
    updatePartMaterials: materials.mutateAsync,
    updatePartProperties: properties.mutateAsync,
    updatePartDisplayName: displayName.mutateAsync,
    updateDoNotProduce: doNotProduce.mutateAsync,
  };
}
